import {
  X,
  Images,
  Timer,
  Maximize2,
  Zap,
  ZapOff,
  HardDriveDownload,
  Share2,
  HelpCircle,
  Crown,
} from "lucide-react";
import { t } from "@/lib/i18n";
import { ASPECT_RATIOS } from "@/components/CameraApp";
import { useOrientation } from "@/lib/orientation";

export default function SettingsMenu({
  onClose,
  // state
  galleryCount,
  isPremium,
  photoCount,
  freeLimit,
  timerSec,
  aspect,
  frontFlash,
  autoSaveEnabled,
  autoShareEnabled,
  showFrontFlash, // boolean: front camera available
  // handlers
  onOpenGallery,
  onCycleTimer,
  onCycleAspect,
  onToggleFrontFlash,
  onToggleAutoSave,
  onToggleAutoShare,
  onShowHint,
  onOpenPaywall,
}) {
  const orientation = useOrientation();
  const isLandscape = orientation !== "portrait";
  const Row = ({ icon: Icon, label, value, onClick, testid, accent }) => (
    <button
      data-testid={testid}
      onClick={() => {
        onClick?.();
      }}
      className="w-full px-5 h-12 flex items-center gap-3 hover:bg-white/5 active:bg-white/10 transition-colors text-left"
    >
      <div className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-white/85" strokeWidth={1.8} />
      </div>
      <span className="flex-1 text-sm text-white/90">{label}</span>
      {value !== undefined && (
        <span
          className={`font-mono-label ${
            accent ? "text-emerald-300" : "text-white/55"
          }`}
        >
          {value}
        </span>
      )}
    </button>
  );

  const aspectLabel = ASPECT_RATIOS.find((a) => a.key === aspect)?.label;

  return (
    <div data-testid="settings-menu" className="fixed inset-0 z-[88]">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
      />
      <div
        className={
          isLandscape
            ? // Center on the screen (same approach as FilterPanel) — clearest
              // placement in landscape regardless of which side the controls
              // bar is mounted on.
              "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] max-h-[92dvh] rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] fade-in overflow-y-auto"
            : "absolute right-4 bottom-24 w-[280px] rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] slide-up overflow-hidden"
        }
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <span className="font-mono-label text-white/55">
            {isPremium
              ? t("premiumBadge")
              : photoCount >= freeLimit
              ? t("freeUsed")
              : t("freeLeft", freeLimit - photoCount)}
          </span>
          <button
            data-testid="settings-close-button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"
          >
            <X className="h-3.5 w-3.5 text-white/85" strokeWidth={1.8} />
          </button>
        </div>

        <div className="py-1 border-t border-white/5">
          <Row
            testid="menu-gallery"
            icon={Images}
            label={t("gallery")}
            value={galleryCount > 0 ? String(galleryCount) : undefined}
            onClick={() => {
              onOpenGallery();
              onClose();
            }}
          />
          {!isPremium && (
            <Row
              testid="menu-paywall"
              icon={Crown}
              label={t("paywallTitle")}
              value="1,29 €"
              accent
              onClick={() => {
                onOpenPaywall();
                onClose();
              }}
            />
          )}
          <Row
            testid="menu-timer"
            icon={Timer}
            label={t("timer", 0).split(":")[0]}
            value={timerSec === 0 ? t("off") : `${timerSec}s`}
            onClick={onCycleTimer}
          />
          <Row
            testid="menu-aspect"
            icon={Maximize2}
            label={t("aspect", "").split(":")[0]}
            value={aspectLabel}
            onClick={onCycleAspect}
          />
          {showFrontFlash && (
            <Row
              testid="menu-frontflash"
              icon={frontFlash ? Zap : ZapOff}
              label={frontFlash ? t("flashOn") : t("flashOff")}
              value={frontFlash ? t("on") : t("off")}
              accent={frontFlash}
              onClick={onToggleFrontFlash}
            />
          )}
          <Row
            testid="menu-autosave"
            icon={HardDriveDownload}
            label={t("autoSave")}
            value={autoSaveEnabled ? t("on") : t("off")}
            accent={autoSaveEnabled}
            onClick={onToggleAutoSave}
          />
          <Row
            testid="menu-autoshare"
            icon={Share2}
            label={t("autoShare")}
            value={autoShareEnabled ? t("on") : t("off")}
            accent={autoShareEnabled}
            onClick={onToggleAutoShare}
          />
          <Row
            testid="menu-help"
            icon={HelpCircle}
            label={t("whereSaved")}
            onClick={() => {
              onShowHint();
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
}
